import React, {
  useState,
  useEffect,
  useReducer,
  useContext,
  useCallback,
  useMemo,
  useRef,
} from "react";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";

import { makeStyles, useTheme } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Button from "@material-ui/core/Button";

import audioNode from "./nodes/audioNode";
import typebotNode from "./nodes/typebotNode";
import openaiNode from "./nodes/openaiNode";
import geminiNode from "./nodes/geminiNode";
import messageNode from "./nodes/messageNode.js";
import startNode from "./nodes/startNode";
import menuNode from "./nodes/menuNode";
import intervalNode from "./nodes/intervalNode";
import imgNode from "./nodes/imgNode";
import randomizerNode from "./nodes/randomizerNode";
import videoNode from "./nodes/videoNode";
import questionNode from "./nodes/questionNode";
import kanbanPhaseNode from "./nodes/kanbanPhaseNode";
import addTagNode from "./nodes/addTagNode";
import aiAgentNode from "./nodes/aiAgentNode";
import webhookNode from "./nodes/webhookNode";

import api from "../../services/api";

import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import {
  Stack,
  Typography,
} from "@mui/material";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { CircularProgress } from "@material-ui/core";

import "reactflow/dist/style.css";
import "./flowbuilder.css";
import ReactFlow, {
  MiniMap,
  Controls,
  Background,
  useNodesState,
  useEdgesState,
  addEdge,
  onElementsRemove,
} from "react-flow-renderer";
import FlowBuilderAddTextModal from "../../components/FlowBuilderAddTextModal";
import FlowBuilderIntervalModal from "../../components/FlowBuilderIntervalModal";
import FlowBuilderConditionModal from "../../components/FlowBuilderConditionModal";
import FlowBuilderMenuModal from "../../components/FlowBuilderMenuModal";
import { Download, Upload } from "lucide-react";
import {
  ContentIcon as FlowContentIcon,
  MenuIcon as FlowMenuIcon,
  RandomizerIcon as FlowRandomizerIcon,
  IntervalIcon as FlowIntervalIcon,
  TicketIcon as FlowTicketIcon,
  QuestionIcon as FlowQuestionIcon,
  KanbanIcon as FlowKanbanIcon,
  TagsIcon as FlowTagsIcon,
  TypebotIcon as FlowTypebotIcon,
  AgentIcon as FlowAgentIcon,
  WebhookIcon as FlowWebhookIcon,
} from "./icons/FlowIcons";
import RemoveEdge from "./nodes/removeEdge";
import FlowBuilderAddImgModal from "../../components/FlowBuilderAddImgModal";
import FlowBuilderTicketModal from "../../components/FlowBuilderAddTicketModal";
import FlowBuilderAddAudioModal from "../../components/FlowBuilderAddAudioModal";

import { useNodeStorage } from "../../stores/useNodeStorage";
import FlowBuilderRandomizerModal from "../../components/FlowBuilderRandomizerModal";
import FlowBuilderAddVideoModal from "../../components/FlowBuilderAddVideoModal";
import FlowBuilderSingleBlockModal from "../../components/FlowBuilderSingleBlockModal";
import singleBlockNode from "./nodes/singleBlockNode";
import { colorPrimary } from "../../styles/styles";
import ticketNode from "./nodes/ticketNode";
import FlowBuilderTypebotModal from "../../components/FlowBuilderAddTypebotModal";
import FlowBuilderOpenAIModal from "../../components/FlowBuilderAddOpenAIModal";
import FlowBuilderGeminiModal from "../../components/FlowBuilderAddGeminiModal";
import FlowBuilderAddQuestionModal from "../../components/FlowBuilderAddQuestionModal";
import FlowBuilderKanbanPhaseModal from "../../components/FlowBuilderKanbanPhaseModal";
import FlowBuilderAddTagModal from "../../components/FlowBuilderAddTagModal";
import FlowBuilderAIAgentModal from "../../components/FlowBuilderAIAgentModal";
import FlowBuilderStartModal from "../../components/FlowBuilderStartModal";
import FlowBuilderWebhookModal from "../../components/FlowBuilderWebhookModal";
import BaseActionButton, { SaveButton } from "../../components/ActionButtons";
import { exportFlow } from "../../services/flowBuilder";
import FlowImportModal from "../../components/FlowImportModal";
import { i18n } from "../../translate/i18n";

const useStyles = makeStyles((theme) => ({
  mainPaper: {
    flex: 1,
    padding: 0,
    position: "relative",
    backgroundColor: "#F8F9FA",
    overflow: "hidden",
  },
  speeddial: {
    backgroundColor: "red",
  },
}));

function geraStringAleatoria(tamanho) {
  var stringAleatoria = "";
  var caracteres =
    "ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789";
  for (var i = 0; i < tamanho; i++) {
    stringAleatoria += caracteres.charAt(
      Math.floor(Math.random() * caracteres.length)
    );
  }
  return stringAleatoria;
}

const nodeTypes = {
  message: messageNode,
  start: startNode,
  menu: menuNode,
  interval: intervalNode,
  img: imgNode,
  audio: audioNode,
  randomizer: randomizerNode,
  video: videoNode,
  singleBlock: singleBlockNode,
  ticket: ticketNode,
  typebot: typebotNode,
  openai: openaiNode,
  gemini: geminiNode,
  question: questionNode,
  kanbanPhase: kanbanPhaseNode,
  addTag: addTagNode,
  aiAgent: aiAgentNode,
  webhook: webhookNode,
};

const edgeTypes = {
  buttonedge: RemoveEdge,
};

const DROP_POPOVER_WIDTH = 170;
const DROP_POPOVER_MAX_HEIGHT = 320;
const DROP_POPOVER_MARGIN = 12;

const clampPopoverPosition = (x, y, width = DROP_POPOVER_WIDTH, height = DROP_POPOVER_MAX_HEIGHT) => {
  const viewportWidth = window.innerWidth || width;
  const viewportHeight = window.innerHeight || height;

  return {
    x: Math.max(DROP_POPOVER_MARGIN, Math.min(Math.max(x, DROP_POPOVER_MARGIN), viewportWidth - width - DROP_POPOVER_MARGIN)),
    y: Math.max(DROP_POPOVER_MARGIN, Math.min(Math.max(y, DROP_POPOVER_MARGIN), viewportHeight - height - DROP_POPOVER_MARGIN)),
  };
};

const initialNodes = [
  {
    id: "1",
    position: { x: 250, y: 100 },
    data: { label: i18n.t("flowBuilderConfig.messages.flowStart") },
    type: "start",
  },
];

const initialEdges = [];

export const FlowBuilderConfig = () => {
  const classes = useStyles();
  const theme = useTheme();
  const history = useHistory();
  const { id } = useParams();

  const storageItems = useNodeStorage();

  const { user } = useContext(AuthContext);

  const isLightMode = theme.mode === "light" || theme.palette?.type === "light";

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [dataNode, setDataNode] = useState(null);
  const [hasMore, setHasMore] = useState(false);
  const [modalAddText, setModalAddText] = useState(null);
  const [modalAddInterval, setModalAddInterval] = useState(false);
  const [modalAddMenu, setModalAddMenu] = useState(null);
  const [modalAddImg, setModalAddImg] = useState(null);
  const [modalAddAudio, setModalAddAudio] = useState(null);
  const [modalAddRandomizer, setModalAddRandomizer] = useState(null);
  const [modalAddVideo, setModalAddVideo] = useState(null);
  const [modalAddSingleBlock, setModalAddSingleBlock] = useState(null);
  const [modalAddTicket, setModalAddTicket] = useState(null);
  const [modalAddTypebot, setModalAddTypebot] = useState(null);
  const [modalAddOpenAI, setModalAddOpenAI] = useState(null);
  const [modalAddGemini, setModalAddGemini] = useState(null);
  const [modalAddQuestion, setModalAddQuestion] = useState(null);
  const [modalAddKanbanPhase, setModalAddKanbanPhase] = useState(null);
  const [modalAddTag, setModalAddTag] = useState(null);
  const [modalAddAIAgent, setModalAddAIAgent] = useState(null);
  const [modalStartNode, setModalStartNode] = useState(null);
  const [modalAddWebhook, setModalAddWebhook] = useState(null);
  const [importModal, setImportModal] = useState(false);

  // Popover ao soltar conexão no vazio
  const [dropPopover, setDropPopover] = useState(null); // { screenX, screenY, sourceId, sourceHandle }
  const [edgeContextMenu, setEdgeContextMenu] = useState(null); // { screenX, screenY, edgeId }
  const [connectionTargetNodeId, setConnectionTargetNodeId] = useState(null);
  const reactFlowWrapper = useRef(null);
  const reactFlowInstance = useRef(null); // capturado via onInit
  const pendingConnectRef = useRef(null); // guarda info da conexão pendente
  const dropPopoverFlowPos = useRef(null); // posição no flow para o novo nó

  const connectionLineStyle = { stroke: "#2b2b2b", strokeWidth: "6px" };

  const addNode = (type, data, overridePos) => {
    const posY = overridePos ? overridePos.y : nodes[nodes.length - 1].position.y;
    const posX = overridePos ? overridePos.x : nodes[nodes.length - 1].position.x + (nodes[nodes.length - 1].width || 200) + 40;
    if (type === "start") {
      return setNodes((old) => {
        return [
        //  ...old.filter((item) => item.id !== "1"),
          {
            id: "1",
            position: { x: posX, y: posY },
            data: { label: i18n.t("flowBuilderConfig.messages.flowStart") },
            type: "start",
          },
        ];
      });
    }
    if (type === "text") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { label: data.text },
            type: "message",
          },
        ];
      });
    }
    if (type === "interval") {
      const unitDisplay = { seconds: "seg.", minutes: "min.", hours: "h", days: "dias" };
      const unit = data.unit || "seconds";
      const value = data.value !== undefined ? data.value : data.sec;
      const label = `${value} ${unitDisplay[unit] || unit}`;
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: {
              label,
              value,
              unit,
              businessHours: data.businessHours || { enabled: false, startTime: "08:00", endTime: "18:00", daysOfWeek: [1,2,3,4,5] }
            },
            type: "interval",
          },
        ];
      });
    }
    if (type === "condition") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: {
              key: data.key,
              condition: data.condition,
              value: data.value,
            },
            type: "condition",
          },
        ];
      });
    }
    if (type === "menu") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: {
              message: data.message,
              arrayOption: data.arrayOption,
            },
            type: "menu",
          },
        ];
      });
    }
    if (type === "img") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { url: data.url },
            type: "img",
          },
        ];
      });
    }
    if (type === "audio") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { url: data.url, record: data.record },
            type: "audio",
          },
        ];
      });
    }
    if (type === "randomizer") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { percent: data.percent },
            type: "randomizer",
          },
        ];
      });
    }
    if (type === "video") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { url: data.url },
            type: "video",
          },
        ];
      });
    }
    if (type === "singleBlock") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "singleBlock",
          },
        ];
      });
    }

    if (type === "ticket") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "ticket",
          },
        ];
      });
    }

    if (type === "typebot") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "typebot",
          },
        ];
      });
    }

    if (type === "openai") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "openai",
          },
        ];
      });
    }

    if (type === "gemini") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "gemini",
          },
        ];
      });
    }

    if (type === "question") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "question",
          },
        ];
      });
    }

    if (type === "kanbanPhase") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "kanbanPhase",
          },
        ];
      });
    }

    if (type === "addTag") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "addTag",
          },
        ];
      });
    }

    if (type === "aiAgent") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: { ...data },
            type: "aiAgent",
          },
        ];
      });
    }
    if (type === "webhook") {
      return setNodes((old) => {
        return [
          ...old,
          {
            id: geraStringAleatoria(30),
            position: { x: posX, y: posY },
            data: {
              webhookPlatform: data.webhookPlatform || "",
              webhookEvent: data.webhookEvent || "",
            },
            type: "webhook",
          },
        ];
      });
    }
  };

  // Wrapper: ao criar nó, se houver conexão pendente, conecta automaticamente
  const addNodeAndConnect = (type, data) => {
    const pending = pendingConnectRef.current;
    const pos = pending ? dropPopoverFlowPos.current : null;
    const newId = geraStringAleatoria(30);

    // Override addNode to use specific ID
    const posY = pos ? pos.y : nodes[nodes.length - 1].position.y;
    const posX = pos ? pos.x : nodes[nodes.length - 1].position.x + (nodes[nodes.length - 1].width || 200) + 40;

    // Map type para nodeType do ReactFlow
    const typeMap = {
      text: "message", interval: "interval", condition: "condition", menu: "menu",
      img: "img", audio: "audio", randomizer: "randomizer", video: "video",
      singleBlock: "singleBlock", ticket: "ticket", typebot: "typebot",
      openai: "openai", gemini: "gemini", question: "question",
      kanbanPhase: "kanbanPhase", addTag: "addTag", aiAgent: "aiAgent",
      webhook: "webhook",
    };
    const nodeType = typeMap[type] || type;

    // Build node data based on type
    let nodeData = { ...data };
    if (type === "text") nodeData = { label: data.text };
    if (type === "interval") {
      const unitDisplay = { seconds: "seg.", minutes: "min.", hours: "h", days: "dias" };
      const unit = data.unit || "seconds";
      const value = data.value !== undefined ? data.value : data.sec;
      nodeData = { label: `${value} ${unitDisplay[unit] || unit}`, value, unit, businessHours: data.businessHours || { enabled: false, startTime: "08:00", endTime: "18:00", daysOfWeek: [1,2,3,4,5] } };
    }
    if (type === "condition") nodeData = { key: data.key, condition: data.condition, value: data.value };
    if (type === "menu") nodeData = { message: data.message, arrayOption: data.arrayOption };
    if (type === "img" || type === "video") nodeData = { url: data.url };
    if (type === "audio") nodeData = { url: data.url, record: data.record };
    if (type === "randomizer") nodeData = { percent: data.percent };

    setNodes((old) => [...old, { id: newId, position: { x: posX, y: posY }, data: nodeData, type: nodeType }]);

    // Se há conexão pendente, criar a edge
    if (pending) {
      setEdges((eds) => {
        if (hasOutgoingConnection(pending.sourceId, eds)) {
          warnSourceAlreadyConnected();
          return eds;
        }

        return addEdge({
          source: pending.sourceId,
          sourceHandle: pending.sourceHandle,
          target: newId,
          targetHandle: null,
          type: "buttonedge",
          animated: true,
          className: "edge-line",
          data: {
            onDelete: (idDel) => setEdges((prev) => prev.filter((ed) => ed.id !== idDel)),
            onContextMenu: openEdgeContextMenu,
          }
        }, eds);
      });
      pendingConnectRef.current = null;
      dropPopoverFlowPos.current = null;
    }

    setDropPopover(null);
  };

  const textAdd = (data) => { addNodeAndConnect("text", data); };
  const intervalAdd = (data) => { addNodeAndConnect("interval", data); };
  const conditionAdd = (data) => { addNodeAndConnect("condition", data); };
  const menuAdd = (data) => { addNodeAndConnect("menu", data); };
  const imgAdd = (data) => { addNodeAndConnect("img", data); };
  const audioAdd = (data) => { addNodeAndConnect("audio", data); };
  const randomizerAdd = (data) => { addNodeAndConnect("randomizer", data); };
  const videoAdd = (data) => { addNodeAndConnect("video", data); };
  const singleBlockAdd = (data) => { addNodeAndConnect("singleBlock", data); };
  const ticketAdd = (data) => { addNodeAndConnect("ticket", data); };
  const typebotAdd = (data) => { addNodeAndConnect("typebot", data); };
  const openaiAdd = (data) => { addNodeAndConnect("openai", data); };
  const geminiAdd = (data) => { addNodeAndConnect("gemini", data); };
  const questionAdd = (data) => { addNodeAndConnect("question", data); };
  const kanbanPhaseAdd = (data) => { addNodeAndConnect("kanbanPhase", data); };
  const addTagAdd = (data) => { addNodeAndConnect("addTag", data); };
  const aiAgentAdd = (data) => { addNodeAndConnect("aiAgent", data); };

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get(`/flowbuilder/flow/${id}`);
          if (data.flow.flow !== null) {
            const flowNodes = data.flow.flow.nodes
            setNodes(flowNodes);
            setEdges(data.flow.flow.connections);
            const filterVariables = flowNodes.filter(nd  => nd.type === "question")
            const variables = filterVariables.map(variable => variable.data.typebotIntegration.answerKey)
            localStorage.setItem('variables', JSON.stringify(variables))
          }
          setLoading(false);
        } catch (err) {
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [id]);

  useEffect(() => {
    if (storageItems.action === "delete") {
      setNodes((old) => old.filter((item) => item.id !== storageItems.node));
      setEdges((old) => {
        const newData = old.filter((item) => item.source !== storageItems.node);
        const newClearTarget = newData.filter(
          (item) => item.target !== storageItems.node
        );
        return newClearTarget;
      });
      storageItems.setNodesStorage("");
      storageItems.setAct("idle");
    }
    if (storageItems.action === "duplicate") {
      const nodeDuplicate = nodes.filter(
        (item) => item.id === storageItems.node
      )[0];
      const maioresX = nodes.map((node) => node.position.x);
      const maiorX = Math.max(...maioresX);
      const finalY = nodes[nodes.length - 1].position.y;
      const nodeNew = {
        ...nodeDuplicate,
        id: geraStringAleatoria(30),
        position: {
          x: maiorX + 240,
          y: finalY,
        },
        selected: false,
        style: { backgroundColor: "transparent", padding: 0, borderRadius: 8 },
      };
      setNodes((old) => [...old, nodeNew]);
      storageItems.setNodesStorage("");
      storageItems.setAct("idle");
    }
  }, [storageItems.action]);

  const loadMore = () => {
    setPageNumber((prevState) => prevState + 1);
  };

  const handleScroll = (e) => {
    if (!hasMore || loading) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - (scrollTop + 100) < clientHeight) {
      loadMore();
    }
  };

  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);

  const nodesWithConnectionTarget = useMemo(
    () =>
      nodes.map((node) => ({
        ...node,
        className: [
          node.className,
          node.id === connectionTargetNodeId ? "connection-drop-target" : null,
        ]
          .filter(Boolean)
          .join(" "),
      })),
    [nodes, connectionTargetNodeId]
  );

  const hasOutgoingConnection = useCallback(
    (sourceNodeId, edgeList = edges) =>
      Boolean(sourceNodeId) && edgeList.some((edge) => edge.source === sourceNodeId),
    [edges]
  );

  const warnSourceAlreadyConnected = useCallback(() => {
    toast.warn("Este card já possui uma conexão de saída.");
  }, []);

  const onConnect = useCallback(
    (params) => {
      setConnectionTargetNodeId(null);
      setEdges((eds) => {
        if (hasOutgoingConnection(params.source, eds)) {
          warnSourceAlreadyConnected();
          return eds;
        }

        return addEdge(
          {
            ...params,
            type: "buttonedge",
            data: {
              onDelete: (idToDelete) => {
                setEdges((prev) =>
                  prev.filter((ed) => ed.id !== idToDelete)
                );
              },
              onContextMenu: openEdgeContextMenu,
            }
          },
          eds
        );
      });
    },
    [hasOutgoingConnection, setEdges, warnSourceAlreadyConnected]
  );

  const onConnectStart = useCallback((event, params) => {
    pendingConnectRef.current = { sourceId: params.nodeId, sourceHandle: params.handleId };
    setConnectionTargetNodeId(null);
  }, []);

  const getDropTargetNodeId = useCallback((clientX, clientY) => {
    if (!clientX && clientX !== 0) return null;
    if (!clientY && clientY !== 0) return null;

    const elements = document.elementsFromPoint
      ? document.elementsFromPoint(clientX, clientY)
      : [document.elementFromPoint(clientX, clientY)];
    const targetNodeElement = elements
      .map((element) => element?.closest?.(".react-flow__node"))
      .find(Boolean);
    const targetNodeId = targetNodeElement?.getAttribute?.("data-id");
    const sourceId = pendingConnectRef.current?.sourceId;

    if (!targetNodeId || targetNodeId === sourceId || hasOutgoingConnection(sourceId)) return null;
    return targetNodeId;
  }, [hasOutgoingConnection]);

  const handleConnectionPreviewMove = useCallback((event) => {
    if (!pendingConnectRef.current) return;

    const point = event.touches?.[0] || event;
    const targetNodeId = getDropTargetNodeId(point.clientX, point.clientY);

    setConnectionTargetNodeId((current) =>
      current === targetNodeId ? current : targetNodeId
    );
  }, [getDropTargetNodeId]);

  const onConnectEnd = useCallback((event) => {
    const target = event.target;
    const targetElement = target?.nodeType === 1 ? target : target?.parentElement;
    if (targetElement?.classList?.contains("react-flow__handle")) {
      pendingConnectRef.current = null;
      setConnectionTargetNodeId(null);
      return;
    }
    const clientX = event.clientX || (event.changedTouches && event.changedTouches[0].clientX);
    const clientY = event.clientY || (event.changedTouches && event.changedTouches[0].clientY);
    if ((!clientX && clientX !== 0) || !pendingConnectRef.current) {
      setConnectionTargetNodeId(null);
      return;
    }

    const targetNodeElement = targetElement?.closest?.(".react-flow__node");
    const targetNodeId = targetNodeElement?.getAttribute?.("data-id");
    if (targetNodeId) {
      const pending = pendingConnectRef.current;
      pendingConnectRef.current = null;
      setConnectionTargetNodeId(null);

      if (targetNodeId !== pending.sourceId) {
        setEdges((eds) => {
          if (hasOutgoingConnection(pending.sourceId, eds)) {
            warnSourceAlreadyConnected();
            return eds;
          }

          return addEdge(
            {
              source: pending.sourceId,
              sourceHandle: pending.sourceHandle,
              target: targetNodeId,
              targetHandle: null,
              type: "buttonedge",
              animated: true,
              className: "edge-line",
              data: {
                onDelete: (idToDelete) => {
                  setEdges((prev) => prev.filter((ed) => ed.id !== idToDelete));
                },
                onContextMenu: openEdgeContextMenu,
              },
            },
            eds
          );
        });
      }
      setDropPopover(null);
      return;
    }

    if (hasOutgoingConnection(pendingConnectRef.current.sourceId)) {
      warnSourceAlreadyConnected();
      pendingConnectRef.current = null;
      setConnectionTargetNodeId(null);
      setDropPopover(null);
      return;
    }

    const wrapper = reactFlowWrapper.current;
    if (!wrapper) return;
    const bounds = wrapper.getBoundingClientRect();

    // Converter coordenadas de tela para coordenadas do flow (com zoom/pan)
    const rfInstance = reactFlowInstance.current;
    const flowPos = rfInstance
      ? rfInstance.project({ x: clientX - bounds.left, y: clientY - bounds.top })
      : { x: clientX - bounds.left, y: clientY - bounds.top };
    dropPopoverFlowPos.current = flowPos;

    const popoverPosition = clampPopoverPosition(clientX, clientY);

    setDropPopover({
      screenX: popoverPosition.x,
      screenY: popoverPosition.y,
      sourceId: pendingConnectRef.current.sourceId,
      sourceHandle: pendingConnectRef.current.sourceHandle,
    });
    setConnectionTargetNodeId(null);
  }, [hasOutgoingConnection, setEdges, warnSourceAlreadyConnected]);


  const saveFlow = async () => {
    await api
      .post("/flowbuilder/flow", {
        idFlow: id,
        nodes: nodes,
        connections: edges,
      })
      .then((res) => {
        toast.success(i18n.t("flowBuilderConfig.messages.flowSaved"));
      });
  };

  const doubleClick = (event, node) => {
    console.log("NODE", node);
    setDataNode(node);
    if (node.type === "message") {
      setModalAddText("edit");
    }
    if (node.type === "interval") {
      setModalAddInterval("edit");
    }

    if (node.type === "menu") {
      setModalAddMenu("edit");
    }
    if (node.type === "img") {
      setModalAddImg("edit");
    }
    if (node.type === "audio") {
      setModalAddAudio("edit");
    }
    if (node.type === "randomizer") {
      setModalAddRandomizer("edit");
    }
    if (node.type === "singleBlock") {
      setModalAddSingleBlock("edit");
    }
    if (node.type === "ticket") {
      setModalAddTicket("edit");
    }
    if (node.type === "typebot") {
      setModalAddTypebot("edit");
    }
    if (node.type === "openai") {
      setModalAddOpenAI("edit");
    }
    if (node.type === "gemini") {
      setModalAddGemini("edit");
    }
    if (node.type === "question") {
      setModalAddQuestion("edit");
    }
    if (node.type === "kanbanPhase") {
      setModalAddKanbanPhase("edit");
    }
    if (node.type === "addTag") {
      setModalAddTag("edit");
    }
    if (node.type === "aiAgent") {
      setModalAddAIAgent("edit");
    }
    if (node.type === "start") {
      setModalStartNode("edit");
    }
    if (node.type === "webhook") {
      setModalAddWebhook("edit");
    }
  };

  const clickNode = (event, node) => {
    setNodes((old) =>
      old.map((item) => {
        if (item.id === node.id) {
          return {
            ...item,
            style: { backgroundColor: "transparent", padding: 1, borderRadius: 8 },
          };
        }
        return {
          ...item,
          style: { backgroundColor: "transparent", padding: 0, borderRadius: 8 },
        };
      })
    );
  };
  const clickEdge = (event, edge) => {
    setEdges((edges) =>
      edges.map((e) =>
        e.id === edge.id
          ? {
              ...e,
              data: {
                ...(e.data || {}),
                selected: true,
                onDelete: (id) => {
                  setEdges((eds) => eds.filter((ed) => ed.id !== id));
                },
                onContextMenu: openEdgeContextMenu,
              }
            }
          : { ...e, data: { ...(e.data || {}), selected: false } }
      )
    );
  };


  const updateNode = (dataAlter) => {
    setNodes((old) =>
      old.map((itemNode) => {
        if (itemNode.id === dataAlter.id) {
          return dataAlter;
        }
        return itemNode;
      })
    );
    setModalAddText(null);
    setModalAddInterval(null);
    setModalAddMenu(null);
    setModalAddOpenAI(null);
    setModalAddTypebot(null);
  };

  const actions = [
    {
      icon: <FlowContentIcon style={{ fontSize: 20 }} />,
      name: i18n.t("flowBuilderConfig.nodes.content"),
      type: "content",
    },
    {
      icon: <FlowMenuIcon style={{ fontSize: 20 }} />,
      name: i18n.t("flowBuilderConfig.nodes.menu"),
      type: "menu",
    },
    {
      icon: <FlowRandomizerIcon style={{ fontSize: 20 }} />,
      name: i18n.t("flowBuilderConfig.nodes.randomizer"),
      type: "random",
    },
    {
      icon: <FlowIntervalIcon style={{ fontSize: 20 }} />,
      name: i18n.t("flowBuilderConfig.nodes.interval"),
      type: "interval",
    },
    {
      icon: <FlowTicketIcon style={{ fontSize: 20 }} />,
      name: i18n.t("flowBuilderConfig.nodes.ticket"),
      type: "ticket",
    },
    {
      icon: <FlowQuestionIcon style={{ fontSize: 20 }} />,
      name: i18n.t("flowBuilderConfig.nodes.question"),
      type: "question",
    },
    {
      icon: <FlowKanbanIcon style={{ fontSize: 20 }} />,
      name: "Fase Kanban",
      type: "kanbanPhase",
    },
    {
      icon: <FlowTagsIcon style={{ fontSize: 20 }} />,
      name: "Adicionar Tag",
      type: "addTag",
    },
    {
      icon: <FlowTypebotIcon style={{ width: 20, height: 20 }} />,
      name: i18n.t("flowBuilderConfig.nodes.typebot"),
      type: "typebot",
    },
    {
      icon: <FlowAgentIcon style={{ width: 20, height: 20 }} />,
      name: "Agente IA",
      type: "aiAgent",
    },
    {
      icon: <FlowWebhookIcon style={{ width: 20, height: 20 }} />,
      name: "Webhook",
      type: "webhook",
    },
  ];

  // Ao selecionar tipo no popover de drop
  const handleDropSelect = (type) => {
    clickActions(type);
    // Não fechar dropPopover aqui - será fechado quando o modal salvar (via addNodeAndConnect)
    // Mas se o user cancelar o modal, limpar pendingConnectRef
  };

  const closeDropPopover = () => {
    setDropPopover(null);
    pendingConnectRef.current = null;
    dropPopoverFlowPos.current = null;
  };

  const closeEdgeContextMenu = () => {
    setEdgeContextMenu(null);
  };

  const handleEdgeContextMenu = (event, edge) => {
    event.preventDefault();
    event.stopPropagation();
    setEdgeContextMenu({
      screenX: event.clientX,
      screenY: event.clientY,
      edgeId: edge.id,
    });
  };

  const openEdgeContextMenu = (event, edgeId) => {
    event.preventDefault();
    event.stopPropagation();
    setEdgeContextMenu({
      screenX: event.clientX,
      screenY: event.clientY,
      edgeId,
    });
  };

  const deleteEdgeFromContextMenu = () => {
    if (!edgeContextMenu?.edgeId) return;
    setEdges((currentEdges) => currentEdges.filter((edge) => edge.id !== edgeContextMenu.edgeId));
    closeEdgeContextMenu();
  };

  const clickActions = (type) => {
    switch (type) {
      case "menu":
        setModalAddMenu("create");
        break;
      case "content":
        setModalAddSingleBlock("create");
        break;
      case "random":
        setModalAddRandomizer("create");
        break;
      case "interval":
        setModalAddInterval("create");
        break;
      case "ticket":
        setModalAddTicket("create");
        break;
      case "typebot":
        setModalAddTypebot("create");
        break;
      case "openai":
        setModalAddOpenAI("create");
        break;
      case "gemini":
        setModalAddGemini("create");
        break;
      case "question":
        setModalAddQuestion("create");
        break;
      case "kanbanPhase":
        setModalAddKanbanPhase("create");
        break;
      case "addTag":
        setModalAddTag("create");
        break;
      case "aiAgent":
        setModalAddAIAgent("create");
        break;
      case "webhook":
        setModalAddWebhook("create");
        break;
      default:
    }
  };

  return (
    <div style={{ width: "100%", height: "100%", overflow: "hidden", display: "flex", flexDirection: "column" }}>
      <FlowBuilderStartModal
        open={modalStartNode}
        onUpdate={updateNode}
        data={dataNode}
        close={setModalStartNode}
      />
      <FlowBuilderWebhookModal
        open={modalAddWebhook}
        onSave={(webhookData) => addNode("webhook", webhookData)}
        onUpdate={updateNode}
        data={dataNode}
        close={setModalAddWebhook}
      />
      <FlowBuilderAddTextModal
        open={modalAddText}
        onSave={textAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddText}
      />
      <FlowBuilderIntervalModal
        open={modalAddInterval}
        onSave={intervalAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddInterval}
      />
      <FlowBuilderMenuModal
        open={modalAddMenu}
        onSave={menuAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddMenu}
      />
      <FlowBuilderAddImgModal
        open={modalAddImg}
        onSave={imgAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddImg}
      />
      <FlowBuilderAddAudioModal
        open={modalAddAudio}
        onSave={audioAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddAudio}
      />
      <FlowBuilderRandomizerModal
        open={modalAddRandomizer}
        onSave={randomizerAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddRandomizer}
      />
      <FlowBuilderAddVideoModal
        open={modalAddVideo}
        onSave={videoAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddVideo}
      />
      <FlowBuilderSingleBlockModal
        open={modalAddSingleBlock}
        onSave={singleBlockAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddSingleBlock}
      />
      <FlowBuilderTicketModal
        open={modalAddTicket}
        onSave={ticketAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddTicket}
      />

      <FlowBuilderOpenAIModal
        open={modalAddOpenAI}
        onSave={openaiAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddOpenAI}
      />

      <FlowBuilderGeminiModal
        open={modalAddGemini}
        onSave={geminiAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddGemini}
      />

      <FlowBuilderTypebotModal
        open={modalAddTypebot}
        onSave={typebotAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddTypebot}
      />

      <FlowBuilderAddQuestionModal
        open={modalAddQuestion}
        onSave={questionAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddQuestion}
      />
      <FlowBuilderKanbanPhaseModal
        open={modalAddKanbanPhase}
        onSave={kanbanPhaseAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddKanbanPhase}
      />

      <FlowBuilderAddTagModal
        open={modalAddTag}
        onSave={addTagAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddTag}
      />

      <FlowBuilderAIAgentModal
        open={modalAddAIAgent}
        onSave={aiAgentAdd}
        data={dataNode}
        onUpdate={updateNode}
        close={setModalAddAIAgent}
      />

      <FlowImportModal open={importModal} onClose={() => setImportModal(false)} />

      {loading && (
        <Stack justifyContent={"center"} alignItems={"center"} height={"100%"}>
          <CircularProgress />
        </Stack>
      )}
      {!loading && (
        <div
          className={`flow-canvas-container${isLightMode ? " flow-light" : ""}`}
          ref={reactFlowWrapper}
          onMouseMoveCapture={handleConnectionPreviewMove}
          onTouchMoveCapture={handleConnectionPreviewMove}
          onMouseLeave={() => setConnectionTargetNodeId(null)}
        >
          <ReactFlow
            nodes={nodesWithConnectionTarget}
            edges={edges}
            deleteKeyCode={["Backspace", "Delete"]}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onNodeDoubleClick={doubleClick}
            onNodeClick={clickNode}
            onEdgeClick={clickEdge}
            onEdgeContextMenu={handleEdgeContextMenu}
            onConnect={onConnect}
            onConnectStart={onConnectStart}
            onConnectEnd={onConnectEnd}
            onInit={(instance) => { reactFlowInstance.current = instance; }}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            fitView
            className="react-flow"
            defaultEdgeOptions={{
              animated: true,
              className: "edge-line"
            }}
          >
            <Controls />
            <MiniMap />
            <Background variant="lines" gap={32} size={1} color="var(--flow-grid-line)" />
          </ReactFlow>

          {/* Sidebar de nodes */}
          <div className="flow-sidebar">
            <div className="flow-sidebar__trigger">
              <span className="flow-sidebar__trigger-dot" />
              <span className="flow-sidebar__trigger-dot" />
              <span className="flow-sidebar__trigger-dot" />
            </div>
            <div className="flow-sidebar__panel">
              <div className="flow-sidebar__panel-inner">
                <p className="flow-sidebar__title">Nodes</p>
                {actions.map((action) => (
                  <div
                    key={action.name}
                    className="flow-sidebar__card"
                    onClick={() => clickActions(action.type)}
                  >
                    <div
                      className="flow-sidebar__card-icon"
                    >
                      {action.icon}
                    </div>
                    <span className="flow-sidebar__card-label">{action.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Popover ao soltar conexão no vazio */}
          {dropPopover && (
            <>
              <div
                onClick={closeDropPopover}
                style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 999 }}
              />
              <div
                className="flow-popover-scroll"
                style={{
                  position: "fixed",
                  left: dropPopover.screenX,
                  top: dropPopover.screenY,
                  zIndex: 1000,
                  background: "var(--flow-popover-bg)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid var(--flow-popover-border)",
                  borderRadius: 10,
                  padding: "6px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  maxHeight: DROP_POPOVER_MAX_HEIGHT,
                  overflowY: "auto",
                  boxShadow: "0 8px 32px var(--flow-panel-shadow)",
                  width: DROP_POPOVER_WIDTH,
                }}
              >
                {actions.filter((action) => {
                  if (action.type === "webhook" && dropPopover.sourceId === "1") return false;
                  return true;
                }).map((action) => (
                  <div
                    key={action.name}
                    onClick={() => handleDropSelect(action.type)}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                      padding: "6px 10px",
                      borderRadius: 6,
                      cursor: "pointer",
                      color: "var(--flow-text)",
                      fontSize: 12,
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.background = "var(--flow-popover-hover)"}
                    onMouseLeave={(e) => e.currentTarget.style.background = "transparent"}
                  >
                    <div style={{ width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center" }}>
                      {action.icon}
                    </div>
                    <span>{action.name}</span>
                  </div>
                ))}
              </div>
            </>
          )}

          {edgeContextMenu && (
            <>
              <div
                onClick={closeEdgeContextMenu}
                onContextMenu={(event) => {
                  event.preventDefault();
                  closeEdgeContextMenu();
                }}
                style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1000 }}
              />
              <div
                style={{
                  position: "fixed",
                  left: edgeContextMenu.screenX,
                  top: edgeContextMenu.screenY,
                  zIndex: 1001,
                  background: "var(--flow-popover-bg)",
                  backdropFilter: "blur(12px)",
                  border: "1px solid var(--flow-popover-border)",
                  borderRadius: 10,
                  padding: 6,
                  boxShadow: "0 8px 32px var(--flow-panel-shadow)",
                  minWidth: 150,
                }}
              >
                <button
                  type="button"
                  onClick={deleteEdgeFromContextMenu}
                  style={{
                    width: "100%",
                    border: 0,
                    background: "transparent",
                    color: "var(--flow-text)",
                    borderRadius: 7,
                    padding: "8px 10px",
                    cursor: "pointer",
                    fontSize: 12,
                    textAlign: "left",
                  }}
                  onMouseEnter={(event) => event.currentTarget.style.background = "var(--flow-popover-hover)"}
                  onMouseLeave={(event) => event.currentTarget.style.background = "transparent"}
                >
                  Excluir conexão
                </button>
              </div>
            </>
          )}

          {/* Botões flutuantes no topo direito */}
          <div className="flow-top-bar">
            <BaseActionButton
              onClick={() => setImportModal(true)}
              borderColor="rgb(139, 92, 246)"
              hoverBg="linear-gradient(to bottom, rgb(167, 139, 250), rgb(139, 92, 246))"
              textColor="#fff"
              iconSvg={<Download size={14} color="#fff" />}
            >
              {i18n.t("flowBuilderConfig.actions.import")}
            </BaseActionButton>
            <BaseActionButton
              onClick={() => exportFlow(id)}
              borderColor="rgb(234, 179, 8)"
              hoverBg="linear-gradient(to bottom, rgb(250, 204, 21), rgb(234, 179, 8))"
              textColor="#fff"
              iconSvg={<Upload size={14} color="#fff" />}
            >
              {i18n.t("flowBuilderConfig.actions.export")}
            </BaseActionButton>
            <SaveButton onClick={saveFlow} textColor="#fff">
              {i18n.t("flowBuilderConfig.actions.save")}
            </SaveButton>
          </div>
        </div>
      )}
    </div>
  );
};
